/**
 * CustomerWorkOrders — read-only list of the customer's active +
 * completed work orders. Each row links into the existing
 * `CustomerWorkOrder` detail page (which is already wired up and
 * enforces the same customerId scope on the server).
 *
 * Backend: `/api/work-orders` already filters by `customerOrgId`
 * when the caller is a customer — no new endpoint needed.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardList, ArrowRight } from 'lucide-react';
import { PageHeader, StatusBadge, PriorityBadge } from '../../components/Shared';
import { getWorkOrders, getSites } from '../../services/api';
import { useSortableList } from '../../hooks/useSortableList';
import { SortHeader, fmtDate } from '../../components/SortHeader';

const COMPARATORS = {
  id:        (o) => o.id || '',
  title:     (o) => (o.title || '').toLowerCase(),
  site:      (o) => (o._siteName || '').toLowerCase(),
  priority:  (o) => ({ high: 3, medium: 2, low: 1 }[o.priority] || 0),
  status:    (o) => o.status || '',
  dueDate:   (o) => o.dueDate || '',
  createdAt: (o) => o.createdAt || '',
};

const CustomerWorkOrders = () => {
  const [orders, setOrders] = useState([]);
  const [sites, setSites] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  // Query-param bootstrapping — the Overview page's "Completed Jobs"
  // tile deep-links here with `?status=completed`. Fall back to
  // 'active' for the default landing view.
  const initial = (searchParams.get('status') || 'active').toLowerCase();
  const [filter, setFilter] = useState(['active', 'completed', 'unscheduled', 'all'].includes(initial) ? initial : 'active');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([getWorkOrders(), getSites().catch(() => [])])
      .then(([o, s]) => { setOrders(o); setSites(s); })
      .finally(() => setLoading(false));
  }, []);

  const siteById = useMemo(() => Object.fromEntries(
    (sites || []).map(s => [s.id, s]),
  ), [sites]);

  // Enrich with siteName so the sort comparator can lean on a plain
  // string. Keeps the comparator dead simple.
  const filtered = useMemo(() => {
    const list = filter === 'all' ? orders
      : filter === 'completed' ? orders.filter(o => o.status === 'completed')
      : filter === 'unscheduled' ? orders.filter(o => o.status === 'unscheduled')
      // "Active" means booked-in jobs — deliberately excludes both
      // completed AND unscheduled (backlog) so it matches what the
      // customer expects to see on the calendar.
      : orders.filter(o => o.status !== 'completed' && o.status !== 'unscheduled');
    return list.map(o => ({ ...o, _siteName: siteById[o.siteId]?.name || '' }));
  }, [orders, filter, siteById]);

  const { sorted, sort, onSort } = useSortableList(filtered, COMPARATORS, {
    initialKey: 'createdAt',
    initialDir: 'desc',
    defaultDescKeys: ['createdAt', 'dueDate', 'priority'],
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="customer-work-orders-page">
      <PageHeader
        title="Active Jobs"
        description="Every work order we've booked in for you. Click through to see the site, dates, RAMS and completion photos."
      />

      <div className="flex flex-wrap gap-2">
        {[
          { key: 'active', label: 'Active' },
          { key: 'unscheduled', label: 'Queued' },
          { key: 'completed', label: 'Completed' },
          { key: 'all', label: 'All' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => {
              setFilter(t.key);
              // Keep the URL in sync so the tab is reload-safe and
              // shareable. Default (`active`) drops the param.
              const next = new URLSearchParams(searchParams);
              if (t.key === 'active') next.delete('status');
              else next.set('status', t.key);
              setSearchParams(next, { replace: true });
            }}
            data-testid={`wo-filter-${t.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              filter === t.key
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
        ) : sorted.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">
            <ClipboardList className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            No {filter === 'all' ? '' : filter} work orders yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <SortHeader label="Ref" sortKey="id" sort={sort} onSort={onSort} />
                <SortHeader label="Title" sortKey="title" sort={sort} onSort={onSort} />
                <SortHeader label="Site" sortKey="site" sort={sort} onSort={onSort} />
                <SortHeader label="Priority" sortKey="priority" sort={sort} onSort={onSort} />
                <SortHeader label="Status" sortKey="status" sort={sort} onSort={onSort} />
                <SortHeader label="Due" sortKey="dueDate" sort={sort} onSort={onSort} />
                <SortHeader label="Created" sortKey="createdAt" sort={sort} onSort={onSort} />
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map(wo => (
                <tr
                  key={wo.id}
                  onClick={() => navigate(`/customer/work-orders/${wo.id}`)}
                  className="hover:bg-slate-50 cursor-pointer"
                  data-testid={`wo-row-${wo.id}`}
                >
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{wo.id}</td>
                  <td className="px-4 py-3 font-medium text-slate-900 max-w-md truncate">{wo.title}</td>
                  <td className="px-4 py-3 text-slate-600 truncate">{wo._siteName || '—'}</td>
                  <td className="px-4 py-3"><PriorityBadge priority={wo.priority} /></td>
                  <td className="px-4 py-3"><StatusBadge status={wo.status} /></td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(wo.dueDate)}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(wo.createdAt)}</td>
                  <td className="px-4 py-3"><ArrowRight className="h-4 w-4 text-slate-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CustomerWorkOrders;
